import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from "@angular/core";
import { AssetViewModel } from "@app/_models/_viewModels/asset.viewmodel";
import { UploadFile, NzMessageService, NzDrawerRef } from "ng-zorro-antd";
import { Media } from "@app/_models/_DTOs/media";
import { environment } from "@environments/environment";
import { NgForm } from "@angular/forms";
import { AssetService } from "@app/_services";
import { HttpEventType } from "@angular/common/http";
import { AssetGroup } from "@app/_models/_DTOs/assetGroup";
import { Department } from "@app/_models/_DTOs/department";
import { Supplier } from "@app/_models/_DTOs/supplier";
import { Employee } from "@app/_models/_DTOs/employee";
import { LocationService } from "@app/_services/location.service";
import { DepartmentService } from "@app/_services/department.service";
import { SupplierService } from "@app/_services/supplier.service";
import { EmployeeService } from "@app/_services/employee.service";
import { AssetGroupService } from "@app/_services/assetGroup.service";
import { ResizeService } from "@app/_services/resize.service";
import { DocumentService } from "@app/_services/document.service";
import { Location } from "@app/_models/_DTOs/location";
import { SCREEN_SIZE } from "@app/_helpers/screen-size.enum";
import { AssetHistoryActionType } from "@app/_models/assetHistoryActionType";
import { AssetHistoryActionName } from "@app/_models/_DTOs/assetHistory";
import { Asset, AssetTypeName } from "@app/_models/_DTOs/asset";
import { AssetType } from "@app/_models/enums/assetType";
import { AutonumericOptions } from "@angularfy/autonumeric/lib/autonumeric.model";
import { ZXingScannerComponent } from "@zxing/ngx-scanner";
import { SupplierViewModel } from "@app/_models/_viewModels/supplier.viewmodel";
import { LegalEntityService } from "@app/_services/legalEntity.service";
import { LegalEntity } from "@app/_models/_DTOs/legalEntity";
import { Observable } from "rxjs";

@Component({
  selector: "app-asset-editor",
  templateUrl: "./asset-editor.component.html",
  styleUrls: ["./asset-editor.component.css"],
})
export class AssetEditorComponent implements OnInit, AfterViewInit {
  @ViewChild("barCodeScanner", { static: false })
  barCodeScanner: ZXingScannerComponent;

  @ViewChild("editDrawer", { static: false })
  editDrawer: NzDrawerRef;

  @Input() hiddenFields: string[];
  @Input() disabledFields: string[];
  @Input() assetViewModel: AssetViewModel;
  @Input() isEditing: boolean;
  @Input() conflictingAssets: Asset[];
  @Input() conflictingAssetNames: string[];
  @Input() selectedLegalEntityId: number = null;
  @Input() legalEntityLock: boolean = false;
  @Output() drawerClosed = new EventEmitter<boolean>();

  public createForm: NgForm;

  @ViewChild("inventoryCodeInputElement", { static: false })
  inventoryCodeInputElement?: ElementRef;

  drawerWidth: string;
  isSupplierModalOpen: boolean = false;
  supplierViewModel: SupplierViewModel = null;

  isSavingAsset: boolean = false;
  isSavingSupplier: boolean = false;
  isEditingSupplier: boolean = false;
  uploadPercentage: number = 0;

  errorMessages = {};

  showUploadList = {
    showPreviewIcon: true,
    showRemoveIcon: true,
    hidePreviewIconInNonImage: true,
  };
  fileList: UploadFile[] = [];
  previewImage: string | undefined = "";
  previewVisible = false;

  apiUrl: string;

  locations: Location[] = [];
  // filteredLocations: Location[] = [];
  assetGroups: AssetGroup[] = [];
  legalEntities: LegalEntity[] = [];
  departments: Department[] = [];
  filteredDepartments: Department[] = [];
  suppliers: Supplier[] = [];
  employees: Employee[] = [];

  public assetTypes = AssetType;

  private delayTimer;

  inventoryCodeOptions: AutonumericOptions = {
    allowDecimalPadding: false,
    decimalPlaces: 0,
    digitGroupSeparator: "",
    leadingZero: "keep",
    maximumValue: "99999",
  };

  purchasePriceOptions: AutonumericOptions = {
    currencySymbol: " EUR",
    currencySymbolPlacement: "s",
    decimalCharacter: ",",
    decimalCharacterAlternative: ".",
    digitGroupSeparator: ".",
    minimumValue: "0",
    negativePositiveSignPlacement: "p",
    outputFormat: "number",
  };

  currentValueOptions: AutonumericOptions = {
    currencySymbol: " EUR",
    currencySymbolPlacement: "s",
    decimalCharacter: ",",
    decimalCharacterAlternative: ".",
    digitGroupSeparator: ".",
    minimumValue: "0",
    negativePositiveSignPlacement: "p",
    outputFormat: "number",
  };

  allowedFormats = [
    "EAN_13",
    "CODE_128",
    "CODABAR",
    "CODE_39",
    "CODE_93",
    "EAN_8",
    "UPC_A",
    "UPC_E",
  ];
  scannerError: string = "";
  barCodeScannerEnabled: boolean = false;

  currencyFormatter = (value: number) => `${value} EUR`;
  currencyParser = (value: string) => value.replace(" EUR", "");

  constructor(
    private assetService: AssetService,
    private locationService: LocationService,
    private departmentService: DepartmentService,
    private legalEntityService: LegalEntityService,
    private supplierService: SupplierService,
    private employeeService: EmployeeService,
    private assetGroupService: AssetGroupService,
    private messageService: NzMessageService,
    private resizeService: ResizeService,
    private documentService: DocumentService
  ) {
    this.apiUrl = environment.apiUrl;
    this.resizeService.onResize$.subscribe((size) => {
      this.drawerWidth = this.resizeService.getDrawerWidth();
    });
  }

  ngOnInit() {
    this.loadDataForDropdowns();
    if (this.resizeService.currentSize != null) {
      this.drawerWidth = this.resizeService.getDrawerWidth();
    }
  }

  ngAfterViewInit() {
    this.editDrawer.afterOpen.subscribe(() => {
      this.loadDataForDropdowns();
    });
  }

  loadDataForDropdowns() {
    this.locationService.getAllBasic().subscribe((data: Location[]) => {
      this.locations = data;
      // this.filteredLocations = data;
    });
    this.employeeService.getAllBasic().subscribe((data: Employee[]) => {
      this.employees = data;
    });
    this.legalEntityService.getAllBasic().subscribe((data: LegalEntity[]) => {
      this.legalEntities = data;
    });
    this.departmentService.getAllBasic().subscribe((data: Department[]) => {
      this.departments = data;
      this.filteredDepartments = data;
      if (this.assetViewModel) {
        if (
          this.assetViewModel.departmentId != null &&
          this.assetViewModel.departmentId != -1
        ) {
          let department = this.departments.find(
            (x) => x.id == this.assetViewModel.departmentId
          );
          if (department && department.legalEntity) {
            this.selectedLegalEntityId = department.legalEntity.id;
          }
        }
      }
      if (this.selectedLegalEntityId != null) {
        this.filteredDepartments = data.filter(
          (x) => x.legalEntity.id == this.selectedLegalEntityId
        );
      }
    });
    this.supplierService.getAllBasic().subscribe((data: Supplier[]) => {
      this.suppliers = data;
    });
    this.assetGroupService.getAllBasic().subscribe((data: AssetGroup[]) => {
      this.assetGroups = data;
    });
  }

  handlePreview = (file: UploadFile) => {
    this.previewImage = file.url || file.thumbUrl;
    this.previewVisible = true;
  };

  previewMedia(media: Media) {
    this.previewImage = `${this.apiUrl}/files/${media.path}`;
    this.previewVisible = true;
  }

  beforeUpload = (file: UploadFile): boolean => {
    this.fileList = this.fileList.concat(file);
    return false;
  };

  deleteAssetMedia(assetMediaItem: Media) {
    this.assetViewModel.media = this.assetViewModel.media.filter(
      (item) => item !== assetMediaItem
    );
  }

  closeDrawer(form?: NgForm, reload?: boolean) {
    this.fileList = [];
    if (form) {
      form.reset();
    }
    this.assetViewModel = null;
    if (reload != null) {
      this.drawerClosed.emit(reload);
    }
    this.drawerClosed.emit(false);
  }

  legalEntityChange() {
    if (this.selectedLegalEntityId != null) {
      this.filteredDepartments = this.departments.filter(
        (x) => x.legalEntity.id == this.selectedLegalEntityId
      );
      if (
        this.assetViewModel &&
        this.assetViewModel.departmentId != null &&
        this.assetViewModel.departmentId != -1
      ) {
        let department = this.departments.find(
          (x) => x.id == this.assetViewModel.departmentId
        );
        if (department.legalEntity.id != this.selectedLegalEntityId) {
          this.assetViewModel.departmentId = null;
        }
      }
    }
  }

  getInventoryCode() {
    if (
      !this.isEditing &&
      this.assetViewModel.type != undefined &&
      this.assetViewModel.departmentId != undefined
    ) {
      let department = this.departments.find(
        (x) => x.id == this.assetViewModel.departmentId
      );
      if (department != undefined && department.legalEntity != undefined) {
        this.assetService
          .getNextInventoryCodeNumber(
            department.legalEntity.id,
            this.assetViewModel.type
          )
          .subscribe((data: number) => {
            this.assetViewModel.inventoryCodeNumber = data;
            this.assetViewModel.inventoryCode = this.pad(data, 4);
          });
      }
    }
  }

  inventoryCodeChange(value: string): void {
    clearTimeout(this.delayTimer);
    let component = this;
    let department = this.departments.find(
      (x) => x.id == this.assetViewModel.departmentId
    );
    if (
      department != undefined &&
      department.legalEntity != undefined &&
      component.assetViewModel.type != undefined
    ) {
      this.delayTimer = setTimeout(function () {
        let inventoryCodeNumber = parseInt(value);
        component.assetService
          .getAssetsWithConflictingInventoryCodeById(
            component.assetViewModel.id,
            department.legalEntity.id,
            component.assetViewModel.type,
            inventoryCodeNumber
          )
          .subscribe((conflictingAssets) => {
            component.conflictingAssets = conflictingAssets;
          });
      }, 500);
    }

    value = this.pad(value, 4);
    this.assetViewModel.inventoryCode = value;
    this.inventoryCodeInputElement!.nativeElement.value =
      this.assetViewModel.inventoryCode;
  }

  barCodeChange(value: string): void {
    this.assetViewModel.barCode = value;
    clearTimeout(this.delayTimer);
    let component = this;
    if (value && value.length > 0) {
      this.delayTimer = setTimeout(function () {
        component.assetService
          .getAssetsByBarcode(value, component.assetViewModel.id)
          .subscribe((x) => {
            x.subscribe(
              (conflictingAssetNames) =>
                (component.conflictingAssetNames = conflictingAssetNames)
            );
          });
      }, 500);
    } else {
      this.conflictingAssetNames = [];
    }
  }

  pad(num, size) {
    var s = num + "";
    while (s.length < size) s = "0" + s;
    return s;
  }

  saveAsset(form: NgForm) {
    this.uploadPercentage = 0;
    this.isSavingAsset = true;
    var purchaseDate = new Date(this.assetViewModel.purchaseDate);
    this.assetViewModel.purchaseDate = purchaseDate.toDateString();
    if (this.isEditing) {
      const formData = new FormData();
      this.fileList.forEach((file: any) => {
        formData.append("files", file);
      });
      formData.append("model", JSON.stringify(this.assetViewModel));

      this.assetService
        .putWithUpload(formData, this.assetViewModel.id)
        .subscribe(
          (event) => {
            if (event.type === HttpEventType.UploadProgress)
              this.uploadPercentage = Math.round(
                (100 * event.loaded) / event.total
              );
            else if (event.type === HttpEventType.Response) {
              this.messageService.create(
                "success",
                "Imovina uspješno izmijenjena"
              );
              this.isSavingAsset = false;
              this.uploadPercentage = 0;
              this.closeDrawer(form, true);
            }
          },
          (error) => {
            if (error.error.ParamName) {
              if (form.controls[error.error.ParamName]) {
                form.controls[error.error.ParamName].setErrors({
                  custom: true,
                });
                var errorMessage =
                  error.error.Message.split("Parameter name")[0];
                this.errorMessages[error.error.ParamName] = errorMessage;
              }
            }
            this.isSavingAsset = false;
          }
        );
    } else {
      const formData = new FormData();
      this.fileList.forEach((file: any) => {
        formData.append("files", file);
      });
      formData.append("model", JSON.stringify(this.assetViewModel));

      this.assetService.postWithUpload(formData).subscribe(
        (event) => {
          if (event.type === HttpEventType.UploadProgress)
            this.uploadPercentage = Math.round(
              (100 * event.loaded) / event.total
            );
          else if (event.type === HttpEventType.Response) {
            this.messageService.create("success", "Imovina uspješno dodana");
            this.isSavingAsset = false;
            this.uploadPercentage = 0;
            this.closeDrawer(form, true);
          }
        },
        (error) => {
          if (error.error.ParamName) {
            if (form.controls[error.error.ParamName]) {
              form.controls[error.error.ParamName].setErrors({ custom: true });
              var errorMessage = error.error.Message.split("Parameter name")[0];
              this.errorMessages[error.error.ParamName] = errorMessage;
            }
          }
          this.isSavingAsset = false;
          this.uploadPercentage = 0;
        }
      );
    }
  }

  openSupplierModal() {
    this.initSupplierModel();
    this.isSupplierModalOpen = true;
  }

  closeSupplierModal() {
    this.supplierViewModel = null;
    this.isSupplierModalOpen = false;
  }

  initSupplierModel(supplier?: Supplier) {
    if (supplier) {
      this.isEditingSupplier = true;
      this.supplierViewModel = {
        id: supplier.id ? supplier.id : -1,
        name: supplier.name ? supplier.name : "",
        pin: supplier.pin ? supplier.pin : undefined,
      };
    } else {
      this.isEditingSupplier = false;
      this.supplierViewModel = {
        id: -1,
        name: "",
        pin: undefined,
      };
    }
  }

  doneEditingSupplier(supplierId?: number) {
    this.closeSupplierModal();
    if (supplierId) {
      this.supplierService.getAllBasic().subscribe((data: Supplier[]) => {
        this.suppliers = data;
        this.assetViewModel.supplierId = supplierId;
      });
    }
  }

  enableScanner() {
    this.scannerError = "";
    this.barCodeScannerEnabled = true;
  }

  disableScanner() {
    this.scannerError = "";
    this.barCodeScannerEnabled = false;
  }

  barCodeScanSuccessHandler(scanResult: string) {
    if (scanResult && scanResult.length > 0) {
      this.assetViewModel.barCode = scanResult;
      let component = this;
      this.delayTimer = setTimeout(function () {
        component.assetService
          .getAssetsByBarcode(scanResult, component.assetViewModel.id)
          .subscribe((assetsWithSameBarcode) => {
            assetsWithSameBarcode.subscribe((conflictingAssetNames) => {
              component.conflictingAssetNames = conflictingAssetNames;
              if (!assetsWithSameBarcode || conflictingAssetNames.length == 0) {
                this.disableScanner();
              }
            });
          });
      }, 500);
    } else {
      this.conflictingAssetNames = [];
    }
  }

  public getActionTypeName(actionType: AssetHistoryActionType) {
    return AssetHistoryActionName.get(actionType).name;
  }

  public getAssetTypeName(assetType: any) {
    return AssetTypeName.get(assetType);
  }

  public assetTypeKeys() {
    var keys = Object.keys(this.assetTypes);
    return keys.slice(keys.length / 2);
  }

  isFieldHidden(fieldName: string) {
    if (this.hiddenFields) {
      if (this.hiddenFields.length > 0) {
        if (this.hiddenFields.some((x) => x == fieldName)) {
          return false;
        }
      }
    }
    return true;
  }

  isFieldEnabled(fieldName: string) {
    if (this.disabledFields) {
      if (this.disabledFields.length > 0) {
        if (this.disabledFields.some((x) => x == fieldName)) {
          return false;
        }
      }
    }
    return true;
  }
}
